import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { SUPPORT_EMAIL } from '../lib/api';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Send } from 'lucide-react';
import { toast } from 'sonner';

const getSubmitButtonLabel = (isLoading, isLoginMode) => {
  if (isLoading) return 'Please wait...';
  return isLoginMode ? 'Sign In' : 'Sign Up';
};

const LoginForm = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const success = isLogin ? await login(username, password) : await register(username, password);
      if (success) {
        toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      } else {
        toast.error(isLogin ? 'Invalid credentials' : 'Registration failed');
      }
    } catch {
      toast.error('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const mailtoForgotPassword = `mailto:${SUPPORT_EMAIL}?subject=Just%20B%20-%20Password%20Reset%20Request&body=Hi%20Brandy%2C%0A%0AI%20need%20my%20password%20reset.%0A%0AMy%20username%20is%3A%20%0A%0AThanks%21`;
  const mailtoHelp = `mailto:${SUPPORT_EMAIL}?subject=Just%20B%20-%20Help`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md mx-4 sm:mx-0">
        <CardHeader className="text-center px-4 sm:px-6">
          <CardTitle className="text-2xl sm:text-3xl font-bold text-justb-primary mb-2">
            Just B. Goal Tracker
          </CardTitle>
          <p className="text-gray-600 text-sm sm:text-base">Transform your aspirations into achievements</p>
        </CardHeader>
        <CardContent className="px-4 sm:px-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username" className="text-sm font-medium">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                placeholder="Enter your username"
                className="mt-1 text-base"
                data-testid="login-username-input"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium">Password</Label>
                {isLogin && (
                  <button
                    type="button"
                    onClick={() => setForgotOpen(true)}
                    className="text-xs text-justb-secondary hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="Enter your password"
                className="mt-1 text-base"
                data-testid="login-password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-justb-primary hover:bg-justb-primary/90 min-h-[48px] text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {getSubmitButtonLabel(loading, isLogin)}
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                onClick={() => setIsLogin(!isLogin)}
                className="text-justb-secondary hover:underline text-sm sm:text-base"
              >
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-500">
              Need help?{' '}
              <a href={mailtoHelp} className="text-justb-secondary hover:underline">
                {SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Forgot your password?</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              No worries — during beta, password resets are handled personally. Email{' '}
              <a href={mailtoForgotPassword} className="text-justb-secondary font-medium hover:underline">
                {SUPPORT_EMAIL}
              </a>{' '}
              with your username, and Brandy will send you a temporary password. You'll usually hear back within 24 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <Button variant="outline" onClick={() => setForgotOpen(false)}>
                Close
              </Button>
              <a
                href={mailtoForgotPassword}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-justb-primary hover:bg-justb-primary/90 text-white text-sm font-medium"
              >
                <Send className="h-4 w-4" />
                Open email
              </a>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginForm;
